import { Menu, MenuItem } from '@rgrd/component-library'
import { Link, useMatch } from 'react-router'

import { useGetCustomersQuery } from '~/shell/api/rtk/generated'
import { getCustomerPath } from '~/shell/app/paths'

import styles from './HospitalPicker.module.css'

/**
 * The active customer's name in the header.
 *
 * Rendered from the caller-scoped `GET /customers` list: an interactive dropdown when more
 * than one customer is accessible, plain non-interactive text otherwise (a single-customer
 * scoped user, an empty list, or while the list is still loading or errored). The picker
 * keys purely on the returned count — it does not branch on the `regard_admin` claim, so
 * the backend's own view of what the caller may reach is the only input.
 *
 * Ported from arc. The customer is read by matching the location rather than with
 * `useParams`, matching how `AppHeader` reads it: the header renders in the layout above
 * the routed page, so one way of reading the segment keeps the two from disagreeing.
 */
export const HospitalPicker = () => {
  const customerMatch = useMatch({ end: false, path: '/c/:customer' })
  const customer = customerMatch?.params.customer
  const { data } = useGetCustomersQuery()
  const customers = data?.customers

  // Until the list resolves, fall back to the route segment as a readable name; it swaps to
  // the display name once the list arrives.
  const activeName =
    customers?.find((entry) => entry.customerRegardId === customer)
      ?.displayName ?? customer

  if (!customers || customers.length <= 1) {
    return <span className={styles.name}>{activeName}</span>
  }

  return (
    <Menu
      label="Select hospital"
      align="end"
      trigger={() => <span className={styles.name}>{activeName}</span>}
    >
      {customers.map((entry) => {
        const to = getCustomerPath(entry.customerRegardId)
        return (
          <MenuItem
            key={entry.customerRegardId}
            to={to}
            selected={entry.customerRegardId === customer}
            // Wire the library item to a react-router Link (anchor-based navigation) while
            // the library owns className / role / aria-current.
            renderItem={(itemProps) => <Link {...itemProps} to={to} />}
          >
            {entry.displayName}
          </MenuItem>
        )
      })}
    </Menu>
  )
}
